import useAuth from '@app/auth/use-auth';
import { CascadingFilterNode } from '@app/components/data-table/cascading-filter';
import { FilterOptions } from '@app/components/data-table/hooks/use-server-filter-options';
import { PickerPeriod } from '@app/components/multi-period-picker';
import { useAccount } from '@shared/api';
import {
  FoodActivitiesList,
  PurchasedGoodModelledAsEnum,
} from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import Unit from '@shared/components/content/unit';
import { ActivityCell } from '@shared/components/data-table/cells/activity-cell';
import { HeaderCell } from '@shared/components/data-table/cells/header-cell';
import { NumberCell } from '@shared/components/data-table/cells/number-cell';
import { TextCell } from '@shared/components/data-table/cells/text-cell';
import RatingCloud from '@shared/components/ratings/rating-cloud';
import { SimpleTooltip } from '@shared/components/simple-tooltip';
import { formatCost, getCurrencySymbol } from '@shared/lib/format';
import { formatSmallNumber } from '@shared/lib/utils';
import { ColumnDef } from '@tanstack/react-table';
import { FoodFilterColumn, FoodFilterOption } from '../filters';
import { NeedsReviewPopover } from './needs-review-popover';

export function usePurchasedFoodAndDrinkTableColumns({
  filters,
  timePeriods,
  taxonomyNodes,
  isTaxonomyLoading,
}: {
  filters: FilterOptions<FoodFilterOption[]>;
  timePeriods: PickerPeriod[];
  /** The product taxonomy on offer, category > subcategory > base product. */
  taxonomyNodes: readonly CascadingFilterNode[];
  isTaxonomyLoading?: boolean;
}): ColumnDef<FoodActivitiesList>[] {
  const { user } = useAuth();
  const hasCostFeature = !!user?.features.cost;
  const { data: account } = useAccount(true);
  const currency = account?.defaultCurrency;

  const columns: ColumnDef<FoodActivitiesList>[] = [
    {
      id: 'uuid',
      accessorKey: 'uuid',
      enableHiding: true,
    },

    {
      id: 'remoteId',
      accessorKey: 'remoteId',
      header: ({ column }) => <HeaderCell column={column} name="Your ID" />,
      cell: ({ getValue }) => {
        return <Text>{getValue<string>()}</Text>;
      },
    },
    {
      id: 'food',
      accessorKey: 'food',
      accessorFn: (row) => row.purchasedGoodInfo,
      header: ({ column }) => <HeaderCell column={column} name="Name" />,
      cell: ({ getValue, row }) => {
        const remoteId = row.original.remoteId;
        const food = getValue<FoodActivitiesList['purchasedGoodInfo']>();
        return (
          <ActivityCell
            className="w-full min-w-52"
            name={food.name}
            remoteId={remoteId!}
          />
        );
      },
      enableColumnFilter: true,
      meta: {
        label: 'Search item',
        variant: 'text',
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
    {
      id: FoodFilterColumn.Category,
      accessorKey: FoodFilterColumn.Category,
      enableColumnFilter: true,
      enableSorting: false,
      meta: {
        label: 'Category',
        variant: 'cascading',
        nodes: taxonomyNodes,
        nodesLoading: isTaxonomyLoading,
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
    {
      id: FoodFilterColumn.Supplier,
      accessorKey: FoodFilterColumn.Supplier,
      enableColumnFilter: true,
      meta: {
        label: 'Supplier',
        variant: 'multiSelect',
        options: filters?.suppliers,
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },

    {
      id: 'matchedFood',
      accessorKey: 'matchedFood',
      accessorFn: (row) => row.purchasedGoodInfo,
      enableSorting: false,
      header: ({ column }) => (
        <HeaderCell column={column} name="Matched food" />
      ),
      cell: ({ getValue, row }) => {
        const modelledAs = row.original.modelledAs;
        const purchasedGood =
          getValue<FoodActivitiesList['purchasedGoodInfo']>();
        const needsReview =
          !!row.original.cleanupResult?.foodNeedsReview &&
          !purchasedGood.userReviewed;

        if (modelledAs === PurchasedGoodModelledAsEnum.modelled_food) {
          return (
            <span className="flex max-w-80 items-center gap-1 truncate text-nowrap">
              <SimpleTooltip
                trigger={
                  <Icons.chefHat className="size-4 shrink-0 text-gray-500" />
                }
                content="This food item has been modelled using a custom recipe"
              />
              <Text includeHover>{purchasedGood.name}</Text>
            </span>
          );
        }

        if (
          modelledAs === PurchasedGoodModelledAsEnum.external_emission_factor
        ) {
          return (
            <span className="flex max-w-80 items-center gap-1 truncate text-nowrap">
              <Icons.arrowRightCircle className="size-4 shrink-0 text-gray-500" />
              <Text className="text-gray-600" includeHover>
                External product assessment
              </Text>
            </span>
          );
        }

        const text =
          modelledAs === PurchasedGoodModelledAsEnum.basic_food ? (
            <Text className="max-w-80 truncate">
              {purchasedGood.basicFoodName}
            </Text>
          ) : (
            <Text>{purchasedGood.name}</Text>
          );

        if (needsReview) {
          return (
            <NeedsReviewPopover itemName={purchasedGood.name} reviewType="food">
              {text}
            </NeedsReviewPopover>
          );
        }
        return text;
      },
      enableColumnFilter: true,
    },

    {
      accessorKey: 'emissionFactor',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Emission Factor"
          numberColumn
          unit={<Unit variant="kgCO2e/kg" />}
        />
      ),
      cell: ({ getValue, row }) => {
        const emissionFactor = getValue<number>();
        const emissionsCategory = row.original.emissionsCategory;

        return (
          <span className="flex items-center justify-end gap-2">
            <TextCell text={formatSmallNumber(emissionFactor)} type="number" />
            <RatingCloud className="w-10" rating={emissionsCategory} />
          </span>
        );
      },
    },
    {
      accessorKey: 'quantity',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Quantity"
          numberColumn
          unit={<Unit variant="kg" />}
        />
      ),
      cell: ({ getValue, row }) => {
        const quantity = getValue<number>();
        const needsReview =
          !!row.original.cleanupResult?.weightNeedsReview &&
          !row.original.purchasedGoodInfo.userReviewed;
        if (needsReview) {
          return (
            <NeedsReviewPopover
              itemName={row.original.purchasedGoodInfo.name}
              reviewType="weight"
              align="right"
            >
              <NumberCell number={quantity} />
            </NeedsReviewPopover>
          );
        }
        return <NumberCell number={quantity} />;
      },
    },
    {
      accessorKey: 'emissions',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Emissions"
          numberColumn
          unit={<Unit variant="tCO2e" />}
        />
      ),
      cell: ({ getValue }) => {
        const emissions = getValue<number>();
        return <NumberCell number={emissions} />;
      },
    },
    {
      id: FoodFilterColumn.Site,
      accessorKey: FoodFilterColumn.Site,
      enableColumnFilter: true,
      meta: {
        label: 'Site',
        variant: 'multiSelect',
        options: filters.sites,
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
    {
      id: FoodFilterColumn.Period,
      accessorKey: FoodFilterColumn.Period,
      meta: {
        label: 'Time period',
        variant: 'periodPicker',
        periods: timePeriods,
        sorting: { ascLabel: 'Oldest', descLabel: 'Newest' },
      },
      enableColumnFilter: true,
    },
    {
      id: FoodFilterColumn.ActivitiesImport,
      accessorKey: FoodFilterColumn.ActivitiesImport,
      enableColumnFilter: true,
      meta: {
        label: 'Import',
        variant: 'multiSelect',
        options: filters.activitiesImport,
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
    {
      id: FoodFilterColumn.Tag,
      accessorKey: FoodFilterColumn.Tag,
      enableColumnFilter: true,
      meta: {
        label: 'Tags',
        variant: 'multiSelect',
        options: filters.tags,
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
  ];

  if (hasCostFeature) {
    columns.push(
      {
        accessorKey: 'totalSpend',
        header: ({ column }) => (
          <HeaderCell column={column} name="Total spend" numberColumn />
        ),
        cell: ({ getValue }) => {
          const totalSpend = getValue<number | null | undefined>();
          return (
            <TextCell text={formatCost(totalSpend, currency)} type="number" />
          );
        },
      },
      {
        accessorKey: 'costPerKg',
        header: ({ column }) => (
          <HeaderCell
            column={column}
            name="Average price"
            numberColumn
            unit={<Unit variant={`${getCurrencySymbol(currency)}/kg`} />}
          />
        ),
        cell: ({ getValue }) => {
          const costPerKg = getValue<number | null | undefined>();
          return (
            <TextCell text={formatCost(costPerKg, currency)} type="number" />
          );
        },
      }
    );
  }

  return columns;
}
